'use client';

/**
 * The key to the pin colours. A stop's colour means ONE thing, its role:
 *   ● Start   ● Overnight   ○ Passing through   ● Destination
 * Same tokens as the list badges and the map pins (planner/roles.ts). Only
 * the roles the trip actually has are listed, always in that order.
 */
import React from 'react';
import { roleLabel } from '../../utils/tripRoles';
import { ROLE_HEX, dayHex, roleAt, type StopRole } from './roles';
import type { TripStop } from './types';

const ORDER: StopRole[] = ['start', 'overnight', 'via', 'end'];
/** Phones: one short word each, so the key fits one line under the chips. */
const SHORT: Record<StopRole, string> = { start: 'Start', overnight: 'Overnight', via: 'Stop', end: 'End' };

type LegendStop = Pick<TripStop, 'kind' | 'nights'> & { lat?: number; lng?: number };

/** A loop: the finish pin sits under the start pin, so the map shows no red pin. */
export function isLoopTrip(stops: LegendStop[]): boolean {
  if (stops.length < 3) return false;
  const a = stops[0];
  const b = stops[stops.length - 1];
  return (
    typeof a.lat === 'number' && typeof b.lat === 'number' && typeof a.lng === 'number' && typeof b.lng === 'number' &&
    Math.abs(a.lat - b.lat) < 0.002 && Math.abs(a.lng - b.lng) < 0.002
  );
}

/** The roles present in a trip, in legend order. A loop's finish is its start. */
export function legendRoles(stops: LegendStop[]): StopRole[] {
  const loop = isLoopTrip(stops);
  const present = new Set(stops.map((_, i) => roleAt(stops, i)));
  if (loop) present.delete('end');
  return ORDER.filter((r) => present.has(r));
}

export default function RoleLegend({
  stops,
  className = '',
  variant = 'map',
  compact = false,
  days,
}: {
  stops: LegendStop[];
  className?: string;
  /** 'map': a floating card on the map. 'inline': a quiet row in the list. */
  variant?: 'map' | 'inline';
  /** Phones: a small key on the map (12px, tighter). */
  compact?: boolean;
  /** Days of driving: each gets a line swatch in its route colour. */
  days?: number;
}) {
  const roles = legendRoles(stops);
  const loop = isLoopTrip(stops);
  if (stops.length < 2 || roles.length < 2) return null;
  const dayList = days && days > 1 ? Array.from({ length: Math.min(days, 7) }, (_, i) => i + 1) : [];
  if (dayList.length) {
    // Pins and road colours are two different things: two labelled rows.
    return (
      <div className={`flex flex-col gap-2 ${className}`} data-testid="role-legend">
        <RoleLegend stops={stops} variant="inline" compact={compact} />
        <ul aria-label="Route colours by day" className="m-0 flex list-none flex-wrap items-center gap-x-3 gap-y-1 p-0 text-tp-sm font-medium text-tp-muted" data-testid="day-legend">
          {dayList.map((d) => (
            <li key={d} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span aria-hidden="true" className="h-1 w-4 flex-none rounded-full" style={{ background: dayHex(d) }} />
              Day {d}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <ul
      aria-label="Pin colours"
      data-testid="role-legend"
      className={`m-0 flex list-none flex-wrap items-center p-0 font-medium text-tp-muted ${
        compact ? 'gap-x-2.5 gap-y-0.5 text-tp-xs' : 'gap-x-3 gap-y-1 text-tp-sm'
      } ${
        variant === 'map'
          ? `border border-solid border-tp-line bg-white shadow-tp-card ${compact ? 'rounded-[14px] px-2.5 py-1' : 'rounded-full px-3 py-1.5'}`
          : ''
      } ${className}`}
    >
      {roles.map((r) => (
        <li key={r} className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {/* Same marks as the pins: solid for places you stop at, pale
              slate with a navy rim for a pass-through. */}
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 flex-none rounded-full"
            style={
              r === 'via'
                ? { background: '#dfe5ee', boxShadow: `inset 0 0 0 2px ${ROLE_HEX.start}` }
                : { background: ROLE_HEX[r] }
            }
          />
          {r === 'start' && loop ? (compact ? 'Start/End' : 'Start & finish') : compact ? SHORT[r] : roleLabel(r)}
        </li>
      ))}
    </ul>
  );
}
